import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    console.error('Error caught by boundary:', error, errorInfo);

    // Detect ChunkLoadError (stale chunks after a new deploy)
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk');
    if (isChunkError) {
      const reloadKey = 'chunk-error-reload';
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1');
        console.log('[ErrorBoundary] ChunkLoadError detected, clearing caches and reloading...');
        if ('caches' in window) {
          caches.keys().then((names) => Promise.all(names.map((n) => caches.delete(n))));
        }
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then((regs) => {
            regs.forEach((reg) => reg.unregister());
          });
        }
        setTimeout(() => window.location.reload(), 100);
        return; // Don't proceed with normal error handling
      }
      // If we already tried reloading, fall through to show error UI with helpful message
    }

    // Store error details in state
    this.setState({
      error,
      errorInfo
    });

    // Log to Sentry with component stack
    Sentry.withScope((scope) => {
      scope.setContext('react', {
        componentStack: errorInfo.componentStack,
      });
      const eventId = Sentry.captureException(error);
      this.setState({ eventId });
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    });
  };

  handleReportFeedback = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    } else {
      // Fallback: Copy error details to clipboard
      const errorDetails = `
Error Report
------------
Error: ${this.state.error?.toString() || 'Unknown error'}
Time: ${new Date().toISOString()}
Component Stack: ${this.state.errorInfo?.componentStack || 'N/A'}
User Agent: ${navigator.userAgent}
`;

      navigator.clipboard.writeText(errorDetails).then(() => {
        alert('Error details copied to clipboard! Please email this to support@yourapp.com');
      }).catch(() => {
        // If clipboard fails, show the error in a dialog
        alert(`Error details:\n\n${errorDetails}\n\nPlease send this to support@yourapp.com`);
      });
    }
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Check if this is a chunk loading error (second attempt — first auto-reload already failed)
      const isChunkError =
        this.state.error?.name === 'ChunkLoadError' ||
        this.state.error?.message?.includes('Loading chunk') ||
        this.state.error?.message?.includes('Loading CSS chunk');

      // Default error UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-icon">⚠️</div>
            <h1>{isChunkError ? 'Update Available' : 'Oops! Something went wrong'}</h1>
            <p className="error-message">
              {isChunkError
                ? 'A new version of Infinity Rockets is available. Please refresh the page to load the latest version.'
                : "We're sorry, but something unexpected happened. Don't worry, your data is safe."}
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error Details (Development Only)</summary>
                <pre className="error-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="error-actions">
              {isChunkError ? (
                <button
                  onClick={() => {
                    sessionStorage.removeItem('chunk-error-reload');
                    if ('caches' in window) {
                      caches.keys().then((names) => Promise.all(names.map((n) => caches.delete(n))));
                    }
                    window.location.reload();
                  }}
                  className="btn-primary"
                >
                  Refresh Now
                </button>
              ) : (
                <>
                  <button onClick={this.handleReset} className="btn-primary">
                    Try Again
                  </button>
                  <button
                    onClick={() => window.location.href = '/'}
                    className="btn-secondary"
                  >
                    Go to Home
                  </button>
                  <button
                    onClick={this.handleReportFeedback}
                    className="btn-secondary"
                  >
                    Report Feedback
                  </button>
                </>
              )}
            </div>

            <p className="error-help">
              If this problem persists, please{' '}
              <button
                onClick={this.handleReportFeedback}
                className="error-link-button"
                style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
              >
                report this error
              </button>
              {' '}or try refreshing the page.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
